import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { makeEnum, makeOptions,
  setupClass, lookupData, 
} from '../Base.js'
import { InputComponent } from '../Common/InputComponent.js'

import { WallAndRoofData } from '../MaterialData/WallsAndRoofs/WallAndRoofData.js'

import { prettyJson, valOr,
  addElem, removeElem, elemIn,
  extendMap,
  downloadTextFile,
  generateItemName,
  sortItemsByName,
  formatNum,
} from '../SharedUtils.js'
import { Units } from '../Common/Units.js'
export { Units } from '../Common/Units.js'
import { Quantity } from '../Common/Quantity.js'
import { makeVector } from '../Common/Math.js'

import {
  FieldType,
  Field,
  FieldGroup,
  ProjectUnits,
} from '../Common/Field.js'

import { gApp, DebugOn } from '../Globals.js'
import { TypesSet } from './TypesSet.js'

export class CustomWallMaterial extends InputComponent {
  init(makeId) {
    this.id = makeId ? gApp.proj().makeId('CustomWallMaterial') : 0;

    this.name = new Field({
      name: 'Name',
      type: FieldType.String,
      note: 'A descriptive name for this material. It will appear in the Material Type dropdown when building a wall or roof.',
    });

    this.rValuePerInch = new Field({
      name: 'R-value per inch',
      type: FieldType.RValuePerInch,
      requiresInput: true,
      note: 'The thermal resistivity of the material — how much R-value it contributes per inch of thickness.',
    });
    this.rValuePerInch.setNameUpdater(() => {
      return gApp.proj().getProjectUnits() === ProjectUnits.Metric
        ? 'R-value per meter' : 'R-value per inch';
    });

    this.density = new Field({
      name: 'Density',
      type: FieldType.Density,
      requiresInput: true,
      note: 'The mass per unit volume of the material, used to calculate the wall weight contribution of this layer.',
    });

    this.serFields = ['id', 'name', 'rValuePerInch', 'density'];
    this.childObjs = '$auto';
    this.objInfo = {
      _name: 'Custom Wall Material',
      path: 'custom-wall-material',
    };
  }

  getTypeId() {
    return `custom_${this.id}`;
  }

  // id is a plain property, not a Field, so FieldCollector would otherwise
  // never show it — but the agent needs getTypeId()'s value to reference this
  // material as a WallLayer's `type` (once `category` is set to 'Custom Materials').
  getAgentSummary() {
    return { typeId: this.getTypeId() };
  }

  requiresUserThickness() {
    return true;
  }

  getResistanceFromThickness(thickness) {
    return this.rValuePerInch.getValueInUnits(Units.RValuePerInch) * thickness;
  }

  getWeightFromThickness(thicknessInches) {
    return this.density.getValueInUnits(Units.PoundsPerFt3) * (thicknessInches / 12.0);
  }
}
setupClass(CustomWallMaterial);

class WallLayer {
  init(wallBuilder, optIsEditable, opts) {
    this.wallBuilder = wallBuilder;
    this.isEditable = valOr(optIsEditable, true)
    this.opts = valOr(opts, {});
    this.isRoof = valOr(this.opts.isRoof, false);

    // Stable identity used by thermal bridges to track which layers they span.
    // Generated once at creation; persisted so bridge selections survive save/load.
    this.uid = gApp.proj().generateUid('WallLayer');

    this._materialInfo = null;

    this.category = new Field({
      name: 'Category',
      type: FieldType.Select,
      choicesFunc: () => {
        let cats = WallAndRoofData.instance().getWallLayerCategories();
        removeElem(cats, 'Defaults');
        if (this.isRoof) {
          removeElem(cats, 'Wall Materials');
        } else {
          removeElem(cats, 'Roofing');
        }
        const lib = gApp.proj().componentsLibrary;
        if (lib && lib.customMaterials.length > 0) {
          cats.push('Custom Materials');
        }
        return cats;
      },
      hiddenChoices: ['Defaults'],
    }) 

    this.type = new Field({
      name: 'Type',
      type: FieldType.Select,
      choices: [],
    })
    this.type.makeChoicesUpdater(() => {
      if (this.category.value === 'Custom Materials') {
        const lib = gApp.proj().componentsLibrary;
        return (lib ? lib.customMaterials : []).map(mat => ({
          label: mat.name.value || '(unnamed)',
          value: mat.getTypeId(),
        }));
      }
      return WallAndRoofData.instance().getWallLayerTypes(
        this.category.value,
        gApp.proj().getProjectVersion()
      );
    })

    let updater = this.wallBuilder.getUpdater();
    updater.addWatchEffect('layer-is-editable', () => {
      this.category.isOutput = !this.isEditable;
      this.type.isOutput = !this.isEditable;
    })
    updater.addWatchEffect('layer-material-info', () => {
      if (this.category.value === 'Custom Materials') {
        const lib = gApp.proj().componentsLibrary;
        this._materialInfo = lib ? lib.lookupCustomMaterial(this.type.value) : null;
      } else {
        this._materialInfo = WallAndRoofData.instance().lookupWallMaterial(
          this.category.value, this.type.value);
      }
    })

    this.thickness = new Field({
      name: 'Thickness',
      type: FieldType.SmallLength,
      min: 0,
      allowMin: true,
    })
    this.thickness.makeUpdater((field) => {
      if (!this._materialInfo) {
        field.isOutput = true;
        field.value = 0.0;
        return;
      }
      if (this._materialInfo.requiresUserThickness()) {
        field.isOutput = false;
      } else {
        field.isOutput = true;
        field.setValueInUnits(this._materialInfo.getPresetThickness(), Units.inches);
      }
    })

    this.rValue = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      isOutput: true,
      min: 0,
      allowMin: true,
    })
    this.rValue.makeUpdater((field) => {
      //console.log(`Updating R-value for wall layer ${this.type.value}`);
      //console.log(`New r-value (imperial): ${this.getRValue()}`);
      field.setValueInUnits(this.getRValue(), Units.RValue);
    })

    this.serFields = [
      'uid',
      'category',
      'type',
      'thickness',
      'rValue',
      'isEditable',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      '_name': 'Wall Layer',
      path: 'wall-layer',
    }
  }

  static makeFirstLayer(wallBuilder) {
    let layer = WallLayer.create(wallBuilder, false, this.opts);
    layer.category.value = "Defaults";
    // NOTE - keep these as the old names for now, to prevent breaking old projects.
    // Conver to use the ID versions later.
    //layer.type.value = "Outdoor_Surface_Resistance";
    layer.type.value = "Outdoor Surface Resistance";
    return layer;
  }

  static makeLastLayer(wallBuilder) {
    let layer = WallLayer.create(wallBuilder, false, this.opts);
    layer.category.value = "Defaults";
    // NOTE - keep these as the old names for now, to prevent breaking old projects.
    // Conver to use the ID versions later.
    //layer.type.value = "Indoor_Layer_Air_Resistance";
    layer.type.value = "Indoor Layer Air Resistance";
    return layer;
  }

  getRValue() {
    if (!this._materialInfo) {
      return 0;
    }
    if (this._materialInfo.requiresUserThickness()) {
      return this._materialInfo.getResistanceFromThickness(
        this.thickness.getValueInUnits(Units.inches));
    } else {
      return this._materialInfo.getPresetResistance();
    }
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    if (this.category.value === 'Custom Materials' && this._materialInfo) {
      typesSet.addType(this._materialInfo, 'customMaterials');
    }
    return typesSet;
  }

  // Get the wall weight per area in lb/ft^2
  getWallWeight() {
    if (!this._materialInfo) {
      return 0
    }
    if (this._materialInfo.requiresUserThickness()) {
      return this._materialInfo.getWeightFromThickness(
        this.thickness.getValueInUnits(Units.inches));
    } else {
      return this._materialInfo.getPresetWeight();
    }
  }
};
setupClass(WallLayer)

class WallBuilder extends InputComponent {
  init(wallThermalResistance, opts) {
    this.wallThermalResistance = wallThermalResistance;
    this.opts = valOr(opts, {});

    let firstLayer = WallLayer.makeFirstLayer(this);
    let lastLayer = WallLayer.makeLastLayer(this);
    this.layers = new ComponentArray(WallLayer, {
      creationFunc: () => { return WallLayer.create(this, true, this.opts); },
      // The last layer is a fixed boundary layer (indoor surface resistance) —
      // new layers must be inserted before it, not appended after.
      insertFunc: (arr, item) => addElem(arr, item, arr.length - 1),
    });
    this.layers.push(firstLayer, lastLayer);

    this.serFields = [
      ser.arrayField('layers', () => {
        return WallLayer.create(this, true, this.opts);
      }),
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      '_name': 'Wall Builder',
      'layers': 'Layer',
      path: 'wall-builder',
    }
  }

  getUpdater() {
    return this.updater;
  }

  addLayer() {
    return this.layers.createNew();
  }

  deleteLayer(layer) {
    removeElem(this.layers, layer);
  }

  moveLayer(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    const [layer] = this.layers.splice(fromIndex, 1);
    this.layers.splice(toIndex, 0, layer);
  }

  getThicknessUnitsStr() {
    return this.layers[0].thickness.getDisplayUnitsLabel();
  }

  getRValueUnitsStr() {
    return this.layers[0].rValue.getDisplayUnitsLabel();
  }

  computeRValue() {
    //console.log(`Computing R-value`);
    let rValue = 0;
    for (const layer of this.layers) {
      rValue += layer.getRValue();
      //console.log(`Layer ${layer.type.value} -  getRValue(): ${layer.getRValue()}`);
      //console.log(`R-value: ${layer.rValue.getValueInUnits(Units.RValue)}`);
      //console.log(`R-value (Metric): ${layer.rValue.getValueInUnits(Units.RValueMetric)}`);
    }
    //console.log(`Total wall R-value: ${rValue}`);
    return rValue;
  }

  getRValueResultStr() {
    let rValue = this.computeRValue();
    return Quantity.create(rValue, Units.RValue).makeValStr(
      {includeUnits: false}
    );
  }

  computeExteriorWallWeight() {
    let wallWeight = 0
    for (const layer of this.layers) {
      wallWeight += layer.getWallWeight();
    }
    return wallWeight;
  }
};
setupClass(WallBuilder)

export let ThermalResistanceEntryMethod = makeEnum({
  Manual: 'Manual',
  BuildWall: 'Build wall',
})

class WallThermalResistance {
  init(wallType, opts) {
    this.opts = valOr(opts, {});
    let isRoof = valOr(this.opts.isRoof, false);

    this.wallType = wallType;

    this.entryMethod = new Field({
      name: 'Entry method',
      type: FieldType.Select,
      choices: [
        {label: 'Manual', value: ThermalResistanceEntryMethod.Manual},
        {label: `Build ${this.opts.isRoof ? 'Roof' : 'Wall'}`, value: ThermalResistanceEntryMethod.BuildWall},
      ],
      bold: true,
      note: 'You can either enter the R-value directly or build the wall/roof by layers (and we calculate the R-value for you).',
    })
    this.manualRValue = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      requiresInput: true,
      note: 'The R-value of the wall/roof. Only relevant when entry method is set to Manual.',
    })
    this.manualRValue.setVisibility(() => {
      return this.entryMethod.value == ThermalResistanceEntryMethod.Manual;
    })
    this.manualExteriorWallWeight = new Field({
      name: isRoof ? 'Roof Weight' : 'Exterior Wall Weight',
      type: FieldType.WeightPerArea,
      min: 0,
      allowMin: false,
      defaultValue: 30,
      metricDefaultValue: 146,
      note: 'The weight of the wall/roof per area. Only relevant for commercial projects with entry method set to Manual.',
    })
    this.manualExteriorWallWeight.setVisibility(() => {
      return gApp.proj().isCommercial() &&
        this.entryMethod.value == ThermalResistanceEntryMethod.Manual;
    })

    this.wallBuilder = WallBuilder.create(this, this.opts);
    this.wallType.updater.setEnabledWhen('wall-builder-enabled', this.wallBuilder, () => {
      return this.entryMethod.value == ThermalResistanceEntryMethod.BuildWall;
    })

    this.thermalBridges = new ComponentArray(ThermalBridge, {
      creationFunc: () => ThermalBridge.create(this, this.opts),
    });

    this.outputRValue = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      isOutput: true,
      note: 'The effective R-value of the wall/roof, calculated from your inputs.',
    })
    this.outputRValue.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.entryMethod.value == ThermalResistanceEntryMethod.Manual) {
        field.waitingInputs = Field.checkWaitingInputs([this.manualRValue]);
      }
      field.setValueInUnits(this.getRValue(), Units.RValue);
    })
    this.outputExteriorWallWeight = new Field({
      name: 'Exterior Wall Weight',
      type: FieldType.WeightPerArea,
      isOutput: true,
      note: 'The weight of the wall per area, calculated from your inputs. Only relevant for commercial projects.',
    })
    this.outputExteriorWallWeight.makeUpdater((field) => {
      field.visible = gApp.proj().isCommercial();
      field.waitingInputs = false;
      if (this.entryMethod.value == ThermalResistanceEntryMethod.Manual) {
        field.waitingInputs = Field.checkWaitingInputs([this.manualExteriorWallWeight]);
      } else if (this.entryMethod.value == ThermalResistanceEntryMethod.BuildWall &&
        this.wallBuilder.layers.length < 3
      ) {
        // If we only have two layers, means we haven't added any actualy materials yet.
        field.waitingInputs = true;
      }
      field.setValueInUnits(this.getWallWeight(), Units.PoundsPerFt2);
    })

    this.serFields = [
      'entryMethod',
      'manualRValue',
      'manualExteriorWallWeight',
      'wallBuilder',
      ser.arrayField('thermalBridges', () => ThermalBridge.create(this, this.opts)),
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: '',
      path: 'thermal-resistance',
    }
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    // Build Wall layers only actually contribute when that's the active entry method.
    if (this.entryMethod.value === ThermalResistanceEntryMethod.BuildWall) {
      for (const layer of this.wallBuilder.layers) {
        typesSet.addTypes(layer.getTypesSet());
      }
    }
    // Thermal bridges apply regardless of entry method (see _applyThermalBridges).
    for (const bridge of this.thermalBridges) {
      typesSet.addTypes(bridge.getTypesSet());
    }
    return typesSet;
  }

  // The effective R-value, including any thermal bridging adjustment.
  getRValue() {
    return this._applyThermalBridges(this._getRValueClear());
  }

  // The clear-field R-value, before any thermal bridging adjustment.
  _getRValueClear() {
    if (this.entryMethod.value == ThermalResistanceEntryMethod.Manual) {
      return this.manualRValue.getValueInUnits(Units.RValue);
    } else {
      return this.wallBuilder.computeRValue();
    }
  }

  // Applies thermal bridging adjustment using the most appropriate method per bridge.
  //
  // For Manual entry walls: always uses parallel path, where the bridge R-value is
  // treated as the full assembly path R-value (outside surface to inside surface).
  //
  // For Build Wall: the method is selected per bridge based on which layers it spans:
  //
  //   Bridge spans ALL editable layers → parallel path method:
  //     Two complete heat-flow paths compete across the full assembly.
  //     R_bridge_path = surface resistances + bridge material
  //     U_eff = f_clear/R_clear + Σ(f_i / R_bridge_path_i)
  //
  //   Bridge spans only SOME editable layers → isothermal planes method:
  //     The bridged layers form a zone where parallel paths compete; all other
  //     layers remain in series and are unaffected by the bridge.
  //     R_zone_eff = 1 / ((1-f)/R_bridged_zone + f/R_bridge_material)
  //     R_eff = R_unbridged_layers + R_zone_eff
  _applyThermalBridges(rClear) {
    if (this.thermalBridges.length === 0 || rClear <= 0) {
      return rClear;
    }
    const isBuiltWall = this.entryMethod.value === ThermalResistanceEntryMethod.BuildWall;

    const layers = isBuiltWall ? this.wallBuilder.layers : null;
    const editableLayers = isBuiltWall ? layers.filter(l => l.isEditable) : null;

    // effectiveRClear is modified in-place by isothermal plane corrections.
    let effectiveRClear = rClear;
    // Accumulates contributions from bridges using the parallel path method.
    let totalParallelFraction = 0;
    let bridgeUSum = 0;

    for (const bridge of this.thermalBridges) {
      const f = bridge.getAreaFraction();
      const rBridgeMaterial = bridge.getBridgeRValue();

      if (!isBuiltWall) {
        // Manual mode: bridge R-value is treated as the full assembly path R-value.
        totalParallelFraction += f;
        if (rBridgeMaterial > 0) {
          bridgeUSum += f / rBridgeMaterial;
        }
        continue;
      }

      const allLayersBridged = editableLayers.length > 0 &&
        editableLayers.every(l => bridge.isLayerBridged(l.uid));

      if (allLayersBridged) {
        // Parallel path: bridge competes against the full clear-field assembly.
        // Bridge path = non-editable surface resistance layers + bridge material.
        totalParallelFraction += f;
        let rSurfaceResistances = 0;
        for (const layer of layers) {
          if (!layer.isEditable) {
            rSurfaceResistances += layer.getRValue();
          }
        }
        const rBridgePath = rSurfaceResistances + rBridgeMaterial;
        if (rBridgePath > 0) {
          bridgeUSum += f / rBridgePath;
        }
      } else {
        // Isothermal planes: bridge competes only against the layers it spans;
        // all other layers remain in series and are not affected.
        let rBridgedZone = 0;
        for (const layer of editableLayers) {
          if (bridge.isLayerBridged(layer.uid)) {
            rBridgedZone += layer.getRValue();
          }
        }
        if (rBridgedZone > 0) {
          const uZone = (1 - f) / rBridgedZone + f / rBridgeMaterial;
          const rZoneEff = uZone > 0 ? 1 / uZone : 0;
          effectiveRClear = effectiveRClear - rBridgedZone + rZoneEff;
        }
      }
    }

    const fClear = Math.max(0, 1 - totalParallelFraction);
    const uEff = fClear / effectiveRClear + bridgeUSum;
    if (uEff <= 0) return rClear;
    return 1 / uEff;
  }

  getWallWeight() {
    if (this.entryMethod.value == ThermalResistanceEntryMethod.Manual) {
      return this.manualExteriorWallWeight.getValueInUnits(Units.PoundsPerFt2);
    } else {
      return this.wallBuilder.computeExteriorWallWeight();
    }
  }
}
setupClass(WallThermalResistance)

export let AbsorptanceEntryMethod = makeEnum({
  Manual: 'Manual',
  SelectByMaterial: 'Select by material',
})

class WallSolarAbsorptance {
  init(opts) {
    this.entryMethod = new Field({
      name: 'Entry method',
      type: FieldType.Select,
      choices: makeOptions(AbsorptanceEntryMethod),
      bold: true,
      note: 'You can either enter the solar absorptance directly or select from a list of predefined materials.',
    })
    this.absorptance = new Field({
      name: 'Absorptance',
      type: FieldType.Ratio,
      min: 0,
      allowMin: false,
      requiresInput: true,
      note: 'The solar absorptance of the wall/roof. Only relevant when entry method is set to Manual.',
    })
    this.absorptance.setVisibility(() => {
      return this.entryMethod.value == AbsorptanceEntryMethod.Manual;
    })
    
    let materialsEnum = WallAndRoofData.instance().MaterialAbsorptance;
    this.material = new Field({
      name: 'Material',
      type: FieldType.Select,
      choices: makeOptions(materialsEnum),
      note: 'The material of the wall/roof, which determines its solar absorptance. Only relevant when entry method is set to Select by material.',
    })
    this.material.setVisibility(() => {
      return this.entryMethod.value == AbsorptanceEntryMethod.SelectByMaterial;
    })

    this.outputAbsorptance = new Field({
      name: 'Absorptance',
      type: FieldType.Ratio,
      isOutput: true,
      allowMin: false,
      note: 'The solar absorptance of the wall/roof, determined from your inputs.',
    })
    this.outputAbsorptance.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.entryMethod.value == AbsorptanceEntryMethod.Manual) {
        field.waitingInputs = Field.checkWaitingInputs([this.absorptance]);
      }
      field.value = this.getAbsorptance();
    })

    this.serFields = [
      'entryMethod',
      'absorptance',
      'material',
      'outputAbsorptance',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      '_name': '',
      path: 'solar-absorptance',
    }
  }

  getAbsorptance() {
    if (this.entryMethod.value == AbsorptanceEntryMethod.Manual) {
      return this.absorptance.value;
    } else {
      let materialsEnum = WallAndRoofData.instance().MaterialAbsorptance;
      return lookupData(materialsEnum._data, [this.material.value]).value;
    }
  }
}
setupClass(WallSolarAbsorptance)

export let ThermalBridgeRValueMethod = makeEnum({
  Manual: 'Manual',
  SelectByMaterial: 'Select by material',
})

export let ThermalBridgeType = makeEnum({
  Manual: 'Manual',
  Recurring: 'Recurring (stud)',
})

class ThermalBridge extends InputComponent {
  init(thermalResistance, opts) {
    this.thermalResistance = thermalResistance;
    this.opts = valOr(opts, {});
    let isRoof = valOr(this.opts.isRoof, false);

    // R-value entry
    this.rValueMethod = Field.makeSelect('R-value entry', ThermalBridgeRValueMethod, {
      bold: true,
    })

    this.manualRValue = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      requiresInput: true,
      note: 'The R-value of the thermal bridge path.',
    })
    this.manualRValue.setVisibility(() => {
      return this.rValueMethod.value == ThermalBridgeRValueMethod.Manual;
    })

    // Select by material (mirrors WallLayer)
    this._materialInfo = null;

    this.materialCategory = new Field({
      name: 'Material Category',
      type: FieldType.Select,
      choicesFunc: () => {
        let cats = WallAndRoofData.instance().getWallLayerCategories();
        removeElem(cats, 'Defaults');
        if (isRoof) {
          removeElem(cats, 'Wall Materials');
        } else {
          removeElem(cats, 'Roofing');
        }
        const lib = gApp.proj().componentsLibrary;
        if (lib && lib.customMaterials.length > 0) {
          cats.push('Custom Materials');
        }
        return cats;
      },
      hiddenChoices: ['Defaults'],
    })
    this.materialCategory.setVisibility(() => {
      return this.rValueMethod.value == ThermalBridgeRValueMethod.SelectByMaterial;
    })

    this.materialType = new Field({
      name: 'Material Type',
      type: FieldType.Select,
      choices: [],
    })
    this.materialType.makeChoicesUpdater(() => {
      if (this.materialCategory.value === 'Custom Materials') {
        const lib = gApp.proj().componentsLibrary;
        return (lib ? lib.customMaterials : []).map(mat => ({
          label: mat.name.value || '(unnamed)',
          value: mat.getTypeId(),
        }));
      }
      return WallAndRoofData.instance().getWallLayerTypes(
        this.materialCategory.value,
        gApp.proj().getProjectVersion()
      );
    })
    this.materialType.setVisibility(() => {
      return this.rValueMethod.value == ThermalBridgeRValueMethod.SelectByMaterial;
    })

    this.updater.addWatchEffect('bridge-material-info', () => {
      if (this.rValueMethod.value == ThermalBridgeRValueMethod.SelectByMaterial) {
        if (this.materialCategory.value === 'Custom Materials') {
          const lib = gApp.proj().componentsLibrary;
          this._materialInfo = lib ? lib.lookupCustomMaterial(this.materialType.value) : null;
        } else {
          this._materialInfo = WallAndRoofData.instance().lookupWallMaterial(
            this.materialCategory.value, this.materialType.value);
        }
      } else {
        this._materialInfo = null;
      }
    })

    this.materialThickness = new Field({
      name: 'Thickness',
      type: FieldType.SmallLength,
      min: 0,
      allowMin: true,
    })
    this.materialThickness.makeUpdater((field) => {
      if (!this._materialInfo || this.rValueMethod.value != ThermalBridgeRValueMethod.SelectByMaterial) {
        field.visible = false;
        field.isOutput = true;
        field.value = 0;
        return;
      }
      field.visible = true;
      if (this._materialInfo.requiresUserThickness()) {
        field.isOutput = false;
      } else {
        field.isOutput = true;
        field.setValueInUnits(this._materialInfo.getPresetThickness(), Units.inches);
      }
    })

    this.outputBridgeRValue = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      isOutput: true,
      note: 'The R-value of the thermal bridge path, calculated from your inputs.',
    })
    this.outputBridgeRValue.setVisibility(() => {
      // Hidden for Manual entry, where the R-value input already shows this value.
      return this.rValueMethod.value != ThermalBridgeRValueMethod.Manual;
    })
    this.outputBridgeRValue.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.rValueMethod.value == ThermalBridgeRValueMethod.Manual) {
        field.waitingInputs = Field.checkWaitingInputs([this.manualRValue]);
      }
      field.setValueInUnits(this.getBridgeRValue(), Units.RValue);
    })

    // Bridge type
    this.bridgeType = Field.makeSelect('Area entry', ThermalBridgeType, {
      bold: true,
    })

    // Recurring (stud) inputs
    this.studThickness = new Field({
      name: 'Stud Thickness',
      type: FieldType.SmallLength,
      min: 0,
      allowMin: false,
      requiresInput: true,
      note: 'Width of the repeating structural member (e.g. 1.5" for a 2x stud).',
    })
    this.studThickness.setVisibility(() => {
      return this.bridgeType.value == ThermalBridgeType.Recurring;
    })

    this.occurrence = new Field({
      name: 'Occurrence',
      type: FieldType.SmallLength,
      min: 0,
      allowMin: false,
      requiresInput: true,
      note: 'Center-to-center spacing of the recurring thermal bridge.',
    })
    this.occurrence.setVisibility(() => {
      return this.bridgeType.value == ThermalBridgeType.Recurring;
    })

    // General area fraction input (0–100%)
    this.areaFractionInput = new Field({
      name: 'Area Fraction',
      type: FieldType.Percent,
      min: 0,
      max: 100,
      allowMin: true,
      requiresInput: true,
      note: 'Percentage of total wall area occupied by the thermal bridge.',
    })
    this.areaFractionInput.setVisibility(() => {
      return this.bridgeType.value == ThermalBridgeType.Manual;
    })

    // Output area fraction (always shown)
    this.outputAreaFraction = new Field({
      name: 'Area Fraction',
      type: FieldType.Percent,
      isOutput: true,
      note: 'The percentage of wall area occupied by the thermal bridge.',
    })
    this.outputAreaFraction.setVisibility(() => {
      // Hidden for Manual entry, where the area fraction input already shows this value.
      return this.bridgeType.value != ThermalBridgeType.Manual;
    })
    this.outputAreaFraction.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.bridgeType.value == ThermalBridgeType.Recurring) {
        field.waitingInputs = Field.checkWaitingInputs([this.studThickness, this.occurrence]);
      } else {
        field.waitingInputs = Field.checkWaitingInputs([this.areaFractionInput]);
      }
      field.value = this.getAreaFraction() * 100;
    })

    // Per-layer bridged state, keyed by wall builder layer uid: true = this bridge
    // spans the layer, false = it does not. Only relevant when the parent wall uses
    // "Build Wall" entry method. A layer with no entry defaults to bridged, so newly
    // added layers are bridged by default without needing to sync against the layer
    // list. Entries for deleted layers are harmless (never read, since uids are unique).
    this.bridgedLayerStates = {};

    this.serFields = [
      'rValueMethod',
      'manualRValue',
      'materialCategory',
      'materialType',
      'materialThickness',
      'bridgeType',
      'studThickness',
      'occurrence',
      'areaFractionInput',
      'bridgedLayerStates',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Thermal Bridge',
      path: 'thermal-bridge',
    }
  }

  // Whether this bridge spans the given wall builder layer. Layers default to
  // bridged when they have no explicit entry.
  isLayerBridged(uid) {
    return this.bridgedLayerStates[uid] !== false;
  }

  setLayerBridged(uid, bridged) {
    this.bridgedLayerStates[uid] = bridged;
  }

  // Returns the R-value of the bridge path in imperial (h·ft²·°F/BTU)
  getBridgeRValue() {
    if (this.rValueMethod.value == ThermalBridgeRValueMethod.Manual) {
      return this.manualRValue.getValueInUnits(Units.RValue);
    }
    if (!this._materialInfo) return 0;
    if (this._materialInfo.requiresUserThickness()) {
      return this._materialInfo.getResistanceFromThickness(
        this.materialThickness.getValueInUnits(Units.inches));
    }
    return this._materialInfo.getPresetResistance();
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    if (this.rValueMethod.value == ThermalBridgeRValueMethod.SelectByMaterial
      && this.materialCategory.value === 'Custom Materials' && this._materialInfo) {
      typesSet.addType(this._materialInfo, 'customMaterials');
    }
    return typesSet;
  }

  // Returns the area fraction as a 0–1 value
  getAreaFraction() {
    if (this.bridgeType.value == ThermalBridgeType.Recurring) {
      let thickness = this.studThickness.getValueInUnits(Units.inches);
      let occ = this.occurrence.getValueInUnits(Units.inches);
      if (occ <= 0) return 0;
      return thickness / occ;
    }
    // Manual: stored as 0-100 percent, return 0-1
    return this.areaFractionInput.value / 100;
  }
}
setupClass(ThermalBridge)

/*
WallTypeOptions:
Curtain wall
Stud wall
EIFS
Brick wall
Concrete block wall
Precast / Cast-in-place block wall
Unknown
*/
export let WallTypeOption = makeEnum({
  Unknown: 'Unknown',
  CurtainWall: 'Curtain wall',
  StudWall: 'Stud wall',
  EIFS: 'EIFS',
  BrickWall: 'Brick wall',
  ConcreteBlockWall: 'Concrete block wall',
  PrecastCastInPlaceBlockWall: 'Precast / Cast-in-place block wall',
});

/*
Roof type options:
Sloped frame roof
Wood deck roof
Metal deck roof
Concrete roof
Unknown
*/
export let RoofTypeOption = makeEnum({
  SlopedFrameRoof: 'Sloped frame roof',
  WoodDeckRoof: 'Wood deck roof',
  MetalDeckRoof: 'Metal deck roof',
  ConcreteRoof: 'Concrete roof',
  Unknown: 'Unknown',
})

export class WallType extends InputComponent {
  init(name, makeId, opts) {
    this.id = makeId ? gApp.proj().makeId('WallType') : 0;
    this.opts = valOr(opts, {});
    
    this.isRoof = valOr(this.opts.isRoof, false);
    this.name = Field.makeName(`${this.isRoof ? 'Roof' : 'Wall'} Name`, name)

    this.type = Field.makeSelect('Wall type', !this.isRoof ? WallTypeOption : RoofTypeOption, {
      defaultValue: 'Unknown',
      requiresInput: false,
      note: 'The type of wall/roof. This is used to determine the CTS vector for the wall/roof.',
    })
    this.type.setVisibility(() => {
      return gApp.proj().isCommercial();
    })
    this.thermalResistance = WallThermalResistance.create(this, this.opts);
    this.solarAbsorptance = WallSolarAbsorptance.create(this.opts);

    this.serFields = [
      'id',
      'name',
      'type',
      'thermalResistance',
      'solarAbsorptance',
    ];

    this.childObjs = '$auto'
    this.objInfo = {
      _uniqueName: true,
      _name: () => {
        return this.name.value;
      },
      uniquePath: true,
      path: () => {
        return `${this.isRoof ? 'roofs' : 'walls'}/${this.id}`;
      }
    }
  }

  getTypesSet() {
    return this.thermalResistance.getTypesSet();
  }

  static getTypeMetadata() {
    return {
      name: 'Wall',
      createType: WallType.createType,
      openEditor: WallType.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = WallType.create(generateItemName('Wall', gApp.proj().wallTypes), true)
    gApp.proj().wallTypes.push(type);
    sortItemsByName(gApp.proj().wallTypes);
    return type
  }

  static openEditor(wallType) {
    let pathName = gApp.proj().isCommercial() ? 'building-walls' : 'walls';
    gApp.proj().editorState.selectedWall = wallType;
    gApp.router.push({name: pathName});
  }

  setFromExcelData(data, importer) {
    console.log("Applying wall data from excel: ", data);
    let wallWeightFieldName = this.isRoof ? 'Roof Weight [lb/ft^2]' : 'Wall Weight [lb/ft^2]';
    importer.applyData(data, {
      'Name': (value) => {
        this.name.setValueAsUser(value);
      },
      'Type': (value) => {
        let enumType = !this.isRoof ? WallTypeOption : RoofTypeOption;
        let enumValue = importer.parseEnumValue(enumType, value);
        if (enumValue) {
          this.type.setValueAsUser(enumValue);
        }
      },
      'R-value': (value) => {
        this.thermalResistance.entryMethod.setValueAsUser(ThermalResistanceEntryMethod.Manual);
        this.thermalResistance.manualRValue.setValueAsUser(parseFloat(value) || 0.0);
      },
      [wallWeightFieldName]: (value) => {
        this.thermalResistance.entryMethod.setValueAsUser(ThermalResistanceEntryMethod.Manual);
        this.thermalResistance.manualExteriorWallWeight.setValueAsUser(parseFloat(value) || 0.0);
      },
      'Solar Absorptance': (value) => {
        this.solarAbsorptance.entryMethod.setValueAsUser(AbsorptanceEntryMethod.Manual);
        this.solarAbsorptance.absorptance.setValueAsUser(parseFloat(value) || 0.0);
      },
    })
  }

  getInputPage() {
    return {
      label: `${this.name.value}`,
      // TODO
      path: `${this.isRoof ? 'roofs' : 'walls'}/${this.id}`,
      typeName: this.isRoof ? 'Roof' : 'Wall',
      typeObject: this,
    };
  }

  getRValue() {
    return this.thermalResistance.getRValue();
  }

  getAbsorptance() {
    return this.solarAbsorptance.getAbsorptance();
  }

  getWallWeight() {
    return this.thermalResistance.getWallWeight();
  }

  getCTSVector() {
    if (!gApp.proj().isCommercial()) {
      return makeVector(24);
    }
    let ctsValues = null;
    if (this.isRoof) {
      ctsValues = gApp.proj().timeSeriesData.getCTSValuesForRoofType(this);
    } else {
      ctsValues = gApp.proj().timeSeriesData.getCTSValuesForWallType(this);
    }
    return ctsValues;
  }

  static getTableInfo() {
    return {
      typeName: 'Wall',
      pagePath: 'walls',
      allowDuplicate: true,
      columns: {
        'name': {
          label: 'Name',
        },
        'rValue': {
          label: 'R-value'
        },
        'absorptance': {
          label: 'Absorptance'
        },
      }
    }
  }

  getTableData() {
    return {
      'name': this.name.value,
      'rValue': this.thermalResistance.outputRValue.getDisplayValueStr(),
      'absorptance': this.solarAbsorptance.outputAbsorptance.getDisplayValueStr(),
    }
  }

  getComponentTableData() {
    const weightLabel = this.isRoof ? 'Roof Weight' : 'Wall Weight';
    const absorptanceField = this.solarAbsorptance.outputAbsorptance;
    const weightField = this.thermalResistance.outputExteriorWallWeight;
    const uValue = Quantity.create(this.thermalResistance.getRValue(), Units.RValue).convertToUnits(Units.UValue);
    const uValueUnits = uValue.getDisplayUnitsLabel();
    return {
      columns: [
        { name: 'Name' },
        { name: 'U-value', units: uValueUnits },
        { name: 'Absorptance', units: absorptanceField.getDisplayUnitsLabel() },
        { name: weightLabel, units: weightField.getDisplayUnitsLabel() },
      ],
      rows: [[
        { value: this.name.value },
        { value: uValue.makeValStr({includeUnits: false}) },
        { value: absorptanceField.getDisplayValueStr() },
        { value: weightField.getDisplayValueStr() },
      ]],
    };
  }

  getConstructionDetailsData() {
    const tr = this.thermalResistance;
    const totalRValue = Quantity.create(tr.getRValue(), Units.RValue);
    const totalUValue = totalRValue.convertToUnits(Units.UValue);
    const rValueUnits = totalRValue.getDisplayUnitsLabel();
    const uValueUnits = totalUValue.getDisplayUnitsLabel();

    const makeQtyCell = (qty, isBold = false) => ({
      value: qty.makeValStr({ includeUnits: false }),
      units: qty.getDisplayUnitsLabel(),
      isBold,
    });

    const buildBridgeRows = () => {
      const bridgeRows = [];
      for (let i = 0; i < tr.thermalBridges.length; i++) {
        const bridge = tr.thermalBridges[i];
        const bridgedAssemblyR = tr.wallBuilder.layers.reduce((sum, layer) => {
          return bridge.isLayerBridged(layer.uid) ? sum : sum + layer.getRValue();
        }, bridge.getBridgeRValue());
        const bridgedAssemblyRQty = Quantity.create(bridgedAssemblyR, Units.RValue);
        const bridgedAssemblyUQty = bridgedAssemblyRQty.convertToUnits(Units.UValue);
        const areaStr = (bridge.getAreaFraction() * 100).toFixed(1) + '%';

        bridgeRows.push([{ value: `Thermal Bridge ${i + 1}`, isBold: true }, { value: '' }]);

        if (bridge.rValueMethod.value === ThermalBridgeRValueMethod.SelectByMaterial) {
          bridgeRows.push([
            { value: '  Material' },
            { value: `${bridge.materialCategory.value} - ${bridge.materialType.value}` },
          ]);
          if (bridge.materialThickness.visible) {
            bridgeRows.push([
              { value: '  Thickness' },
              { value: bridge.materialThickness.getDisplayValueStr(), units: bridge.materialThickness.getDisplayUnitsLabel() },
            ]);
          }
        }

        bridgeRows.push([{ value: '  Bridge R-value' }, makeQtyCell(Quantity.create(bridge.getBridgeRValue(), Units.RValue))]);
        bridgeRows.push([{ value: '  Bridged Assembly R-value' }, makeQtyCell(bridgedAssemblyRQty)]);
        bridgeRows.push([{ value: '  Bridged Assembly U-value' }, makeQtyCell(bridgedAssemblyUQty)]);

        if (bridge.bridgeType.value === ThermalBridgeType.Recurring) {
          bridgeRows.push([
            { value: '  Stud Width' },
            { value: bridge.studThickness.getDisplayValueStr(), units: bridge.studThickness.getDisplayUnitsLabel() },
          ]);
          bridgeRows.push([
            { value: '  Stud Spacing' },
            { value: bridge.occurrence.getDisplayValueStr(), units: bridge.occurrence.getDisplayUnitsLabel() },
          ]);
        }

        bridgeRows.push([{ value: '  Framing Percentage' }, { value: areaStr }]);
      }
      return bridgeRows;
    };

    if (tr.entryMethod.value === ThermalResistanceEntryMethod.Manual) {
      const manualRQty = Quantity.create(tr._getRValueClear(), Units.RValue);
      const manualUQty = manualRQty.convertToUnits(Units.UValue);
      return {
        columns: [{ name: 'Property' }, { name: 'Value' }],
        rows: [
          [{ value: 'Entry method' }, { value: 'Manual entry' }],
          [{ value: 'Final R-value', isBold: true }, makeQtyCell(manualRQty, true)],
          [{ value: 'Final U-value', isBold: true }, makeQtyCell(manualUQty, true)],
          ...buildBridgeRows(),
        ],
        showHeader: false,
      };
    }

    const layerRows = tr.wallBuilder.layers.map(layer => {
      const layerRQty = Quantity.create(layer.getRValue(), Units.RValue);
      return [
        { value: layer.type.getSelectLabelStr() },
        makeQtyCell(layerRQty),
      ];
    });

    const resultRows = [...layerRows];
    if (tr.thermalBridges.length > 0) {
      const clearFieldRQty = Quantity.create(tr._getRValueClear(), Units.RValue);
      const clearFieldUQty = clearFieldRQty.convertToUnits(Units.UValue);

      resultRows.push([
        { value: 'Unbridged Portion', isBold: true },
        makeQtyCell(clearFieldRQty, true),
      ]);
      resultRows.push([
        { value: 'Unbridged Portion U-value' },
        makeQtyCell(clearFieldUQty),
      ]);

      for (let i = 0; i < tr.thermalBridges.length; i++) {
        const bridge = tr.thermalBridges[i];
        const bridgedAssemblyR = tr.wallBuilder.layers.reduce((sum, layer) => {
          return bridge.isLayerBridged(layer.uid) ? sum : sum + layer.getRValue();
        }, bridge.getBridgeRValue());
        const bridgedAssemblyRQty = Quantity.create(bridgedAssemblyR, Units.RValue);
        const bridgedAssemblyUQty = bridgedAssemblyRQty.convertToUnits(Units.UValue);
        const areaStr = (bridge.getAreaFraction() * 100).toFixed(1) + '%';

        resultRows.push([
          { value: `Thermal Bridge ${i + 1} Bridge R-value`, isBold: true },
          makeQtyCell(Quantity.create(bridge.getBridgeRValue(), Units.RValue), true),
        ]);
        resultRows.push([
          { value: `Thermal Bridge ${i + 1} Bridged Assembly R-value`, isBold: true },
          makeQtyCell(bridgedAssemblyRQty, true),
        ]);
        resultRows.push([
          { value: `Thermal Bridge ${i + 1} Bridged Assembly U-value` },
          makeQtyCell(bridgedAssemblyUQty),
        ]);
        resultRows.push([
          { value: `Thermal Bridge ${i + 1} Framing Percentage`, isBold: true },
          { value: areaStr, isBold: true },
        ]);
      }
    }

    resultRows.push([
      { value: 'Final R-value', isBold: true },
      makeQtyCell(totalRValue, true),
    ]);
    resultRows.push([
      { value: 'Final U-value', isBold: true },
      makeQtyCell(totalUValue, true),
    ]);

    return {
      columns: [
        { name: 'Layer' },
        { name: 'R-value', units: rValueUnits },
      ],
      rows: resultRows,
    };
  }
};
setupClass(WallType)

export class RoofType extends WallType {
  init(name, makeId) {
    super.init(name, makeId, {isRoof: true});
  }

  static getTypeMetadata() {
    return {
      name: 'Roof',
      createType: RoofType.createType,
      openEditor: RoofType.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = RoofType.create(generateItemName('Roof', gApp.proj().roofTypes), true)
    gApp.proj().roofTypes.push(type);
    sortItemsByName(gApp.proj().roofTypes);
    return type
  }

  static openEditor(roofType) {
    let pathName = gApp.proj().isCommercial() ? 'building-roofs' : 'roofs';
    gApp.proj().editorState.selectedRoof = roofType;
    gApp.router.push({name: pathName});
  }

  static getTableInfo() {
    return {
      typeName: 'Roof',
      pagePath: 'roofs',
      allowDuplicate: true,
      columns: {
        'name': {
          label: 'Name',
        },
        'rValue': {
          label: 'R-value'
        },
        'absorptance': {
          label: 'Absorptance'
        },
      }
    }
  }
};
setupClass(RoofType)
